/**
 * PatrolFlow - Main React Application Component
 *
 * Copyright (c) 2025 PatrolFlow. All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying, distribution,
 * modification, or use of this software is strictly prohibited without explicit
 * written permission from the copyright owner.
 *
 * Licensed under proprietary license. See LICENSE file for details.
 *
 * @author Tom Hough
 * @version 1.0.0
 * @created July 2025
 */

import React, { lazy, Suspense, useEffect } from "react";
import "@/styles/landing-approved.css";
import { Switch, Route, Redirect, useLocation } from "wouter";
import { queryClient, preloadCommonQueries } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { ToastAction } from "@/components/ui/toast";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useHideBrandLoader } from "@/hooks/useHideBrandLoader";
import { lazyWithRetry } from "@/utils/lazyWithRetry";
import { OnboardingProvider } from "@/contexts/OnboardingContext";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { FloSupportWidget } from "@/components/support/FloSupportWidget";
import { BrandLoader } from "@/components/BrandLoader";
import { FEATURES } from "@/config/features";
import { CLIENT_ROUTES } from "@shared/clientRoutes";

function SubscriptionLapsedNotifier() {
  const { toast } = useToast();

  useEffect(() => {
    const showSubscriptionLapsed = (event: Event) => {
      const { message, billingPath } = (event as CustomEvent<{
        message?: string;
        billingPath?: string;
      }>).detail ?? {};

      toast({
        title: "Account Limited",
        description: message ?? "Your account is read-only — subscribe to restore access",
        variant: "destructive",
        action: (
          <ToastAction
            altText="Open billing"
            onClick={() => window.location.assign(billingPath ?? "/billing")}
          >
            Open billing
          </ToastAction>
        ),
      });
    };

    window.addEventListener("subscription-lapsed", showSubscriptionLapsed);
    return () => window.removeEventListener("subscription-lapsed", showSubscriptionLapsed);
  }, [toast]);

  return null;
}

// =============================================================================
// PUBLIC ROUTES (Minimal bundle - only what's needed for landing/auth)
// =============================================================================
const Landing = lazyWithRetry(() => import("@/pages/landing"));
const AuthPage = lazyWithRetry(() => import("@/pages/auth-page"));
const Pricing = lazyWithRetry(() => import("@/pages/pricing"));
const Terms = lazyWithRetry(() => import("@/pages/terms"));
const Privacy = lazyWithRetry(() => import("@/pages/privacy"));
const EmployerGuide = lazyWithRetry(() => import("@/pages/employer-guide"));
const Walinzi = lazyWithRetry(() => import("@/pages/walinzi"));
const DeleteAccount = lazyWithRetry(() => import("@/pages/delete-account"));
const About = lazyWithRetry(() => import("@/pages/about"));
const Contact = lazyWithRetry(() => import("@/pages/contact"));
const TrialSignup = lazyWithRetry(() => import("@/pages/trial-signup"));
const ForgotPassword = lazyWithRetry(() => import("@/pages/forgot-password"));
const ResetPassword = lazyWithRetry(() => import("@/pages/reset-password"));

const publicRouteMetadata: Record<
  string,
  { title: string; description: string; indexable: boolean }
> = {
  "/": {
    title: "NightKept - See what you're paying for",
    description:
      "A timestamped, GPS-recorded route of where your guards patrolled - reviewed from your dashboard, checkpoint by checkpoint. Guard patrol management for Kenya.",
    indexable: true,
  },
  "/pricing": {
    title: "NightKept Pricing | Simple Site-Based Plans",
    description:
      "See NightKept's transparent, site-based pricing for GPS-recorded guard patrol routes, checkpoint verification, and patrol history in Kenya.",
    indexable: true,
  },
  "/trial-signup": {
    title: "Start Your 14-Day Free Trial | NightKept",
    description:
      "Try NightKept free for 14 days. Record GPS patrol routes, verify checkpoints, and review patrol activity from your dashboard. No card required.",
    indexable: true,
  },
  "/terms": {
    title: "Terms of Service | NightKept",
    description:
      "Read the NightKept Terms of Service for GPS-based security patrol tracking, subscriptions, billing, account use, and customer responsibilities.",
    indexable: true,
  },
  "/privacy": {
    title: "Privacy Policy | NightKept",
    description:
      "Learn how NightKept collects, uses, protects, and shares account, business, GPS location, patrol, and technical information.",
    indexable: true,
  },
  "/employer-guide": {
    title: "Using NightKept fairly: a guide for employers",
    description: "Using NightKept fairly: a guide for employers",
    indexable: true,
  },
  "/walinzi": {
    title: "NightKept: Taarifa kwa walinzi",
    description: "NightKept: Taarifa kwa walinzi",
    indexable: true,
  },
  "/delete-account": {
    title: "Delete Account or Request Data | NightKept",
    description:
      "Request deletion of your NightKept account and personal data, or ask for a copy of your data.",
    indexable: true,
  },
  "/auth": {
    title: "Sign In | NightKept",
    description: "Sign in to your NightKept account.",
    indexable: false,
  },
  "/forgot-password": {
    title: "Reset Your Password | NightKept",
    description: "Request a password reset for your NightKept account.",
    indexable: false,
  },
  "/reset-password": {
    title: "Choose a New Password | NightKept",
    description: "Choose a new password for your NightKept account.",
    indexable: false,
  },
};

function RouteMetadata() {
  const [location] = useLocation();

  useEffect(() => {
    const path = location.length > 1 ? location.replace(/\/+$/, "") : location;
    const page = publicRouteMetadata[path] ?? {
      title: "NightKept Application",
      description: "NightKept secure patrol management application.",
      indexable: false,
    };
    const canonicalUrl = `https://www.nightkept.ke${path}`;
    const setMeta = (selector: string, content: string) => {
      document.querySelector<HTMLMetaElement>(selector)?.setAttribute("content", content);
    };

    document.title = page.title;
    setMeta('meta[name="description"]', page.description);
    setMeta('meta[name="robots"]', page.indexable ? "index, follow" : "noindex, nofollow");
    setMeta('meta[property="og:title"]', page.title);
    setMeta('meta[property="og:description"]', page.description);
    setMeta('meta[name="twitter:title"]', page.title);
    setMeta('meta[name="twitter:description"]', page.description);

    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (page.indexable) {
      const link = canonical ?? document.head.appendChild(document.createElement("link"));
      link.rel = "canonical";
      link.href = canonicalUrl;
      setMeta('meta[property="og:url"]', canonicalUrl);
      setMeta('meta[name="twitter:url"]', canonicalUrl);
    } else {
      canonical?.remove();
    }
  }, [location]);

  return null;
}

// =============================================================================
// AUTHENTICATED ROUTES (Load only when logged in)
// =============================================================================
// ⚠️ CRITICAL: Dashboard and AdminDashboard are NOT imported at top level
// They contain Mapbox (1.6MB) and must only load when user navigates to them
// Inline lazy loading in routes ensures they don't bundle with /auth page
const Setup = lazyWithRetry(() => import("@/pages/setup"));
const GuardHistory = lazyWithRetry(() => import("@/pages/guard-history"));
const PatrolDetail = lazyWithRetry(() => import("@/pages/patrol-detail"));
const ShiftScheduling = lazyWithRetry(() => import("@/pages/shift-scheduling"));
const ProfilePage = lazyWithRetry(() => import("@/pages/profile"));

// =============================================================================
// ADMIN ROUTES (Heavy features - load only for admin users)
// =============================================================================
// ⚠️ AdminDashboard also removed from top-level to prevent preloading
const Billing = lazyWithRetry(() => import("@/pages/billing"));
const SuperAdmin = lazyWithRetry(() => import("@/pages/super-admin"));
const CustomerManagement = lazyWithRetry(
  () => import("@/pages/customer-management"),
);
const AdminCompanyViewer = lazyWithRetry(() => import("@/pages/admin-company-viewer"));

// =============================================================================
// ERROR ROUTES (Always available)
// =============================================================================
const NotFound = lazyWithRetry(() => import("@/pages/not-found"));

// Helper component to handle conditional routes with Suspense and ErrorBoundary
const ProtectedRoute = ({
  isAuthenticated,
  PublicComponent,
  ProtectedComponent,
  requireSuperAdmin = false,
}: {
  isAuthenticated: boolean;
  PublicComponent: React.ComponentType;
  ProtectedComponent: React.ComponentType;
  requireSuperAdmin?: boolean;
}) => {
  const { user, isLoading } = useAuth();
  const { toast } = useToast();
  const [, navigate] = useLocation();

  useEffect(() => {
    if (!requireSuperAdmin) return;
    if (isLoading || !isAuthenticated) return;
    if (user?.isSuperAdmin) return;
    toast({
      title: "Access Denied",
      description: "Internal staff access required.",
      variant: "destructive",
    });
    navigate("/");
  }, [requireSuperAdmin, isLoading, isAuthenticated, user, toast, navigate]);

  if (requireSuperAdmin) {
    if (isLoading || !isAuthenticated || !user?.isSuperAdmin) return null;
  }

  const Component = isAuthenticated ? ProtectedComponent : PublicComponent;
  return (
    <ErrorBoundary>
      <Suspense fallback={<BrandLoader />}>
        <Component />
      </Suspense>
    </ErrorBoundary>
  );
};

// Helper component for public routes with ErrorBoundary
const PublicRoute = ({
  component: Component,
}: {
  component: React.ComponentType;
}) => (
  <ErrorBoundary>
    <Suspense fallback={<BrandLoader />}>
      <Component />
    </Suspense>
  </ErrorBoundary>
);

// Helper component for truly lazy-loaded routes (loaded inline, not at module level)
const LazyRoute = ({
  isAuthenticated,
  importPath,
  requireSuperAdmin = false,
}: {
  isAuthenticated: boolean;
  importPath: string;
  requireSuperAdmin?: boolean;
}) => {
  // Create the lazy component inline - this ensures it's not evaluated until navigation
  const LazyComponent = React.useMemo(
    () =>
      lazyWithRetry(() => {
        // Map string path to actual import
        switch (importPath) {
          case "dashboard":
            return import("@/pages/dashboard");
          case "admin-dashboard":
            return import("@/pages/admin-dashboard");
          default:
            throw new Error(`Unknown import path: ${importPath}`);
        }
      }),
    [importPath],
  );

  return (
    <ProtectedRoute
      isAuthenticated={isAuthenticated}
      PublicComponent={AuthPage}
      ProtectedComponent={LazyComponent}
      requireSuperAdmin={requireSuperAdmin}
    />
  );
};

function RoutedContent({ children }: { children: React.ReactNode }) {
  useHideBrandLoader();
  return <>{children}</>;
}

function Router() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <BrandLoader />;
  }

  return (
    <Suspense fallback={<BrandLoader />}>
      <RouteMetadata />
      <RoutedContent>
        <Switch>
          {/* ========================================================================= */}
          {/* PUBLIC ROUTES - Minimal JavaScript bundle for landing/marketing */}
      {/* ========================================================================= */}
      <Route
        path={CLIENT_ROUTES.home}
        component={() => {
          // Dashboard loaded inline ONLY when navigating to /
          if (isAuthenticated) {
            return <LazyRoute isAuthenticated={true} importPath="dashboard" />;
          }
          return <PublicRoute component={Landing} />;
        }}
      />

      <Route
        path={CLIENT_ROUTES.auth}
        component={() => <PublicRoute component={AuthPage} />}
      />
      <Route
        path={CLIENT_ROUTES.pricing}
        component={() => <PublicRoute component={Pricing} />}
      />
      <Route
        path={CLIENT_ROUTES.terms}
        component={() => <PublicRoute component={Terms} />}
      />
      <Route
        path={CLIENT_ROUTES.privacy}
        component={() => <PublicRoute component={Privacy} />}
      />
      <Route path={CLIENT_ROUTES.employerGuide} component={() => <PublicRoute component={EmployerGuide} />} />
      <Route path={CLIENT_ROUTES.walinzi} component={() => <PublicRoute component={Walinzi} />} />
      <Route
        path={CLIENT_ROUTES.deleteAccount}
        component={() => <PublicRoute component={DeleteAccount} />}
      />
      <Route
        path={CLIENT_ROUTES.about}
        component={() => <PublicRoute component={About} />}
      />
      <Route
        path={CLIENT_ROUTES.contact}
        component={() => <PublicRoute component={Contact} />}
      />
      <Route
        path={CLIENT_ROUTES.trialSignup}
        component={() => <PublicRoute component={TrialSignup} />}
      />
      <Route
        path={CLIENT_ROUTES.forgotPassword}
        component={() => <PublicRoute component={ForgotPassword} />}
      />
      <Route
        path={CLIENT_ROUTES.resetPassword}
        component={() => <PublicRoute component={ResetPassword} />}
      />

      {/* ========================================================================= */}
      {/* AUTHENTICATED ROUTES - Load only when user is logged in */}
      {/* ========================================================================= */}

      {/* Phase 1: Redirect old /dashboard to / */}
      <Route path={CLIENT_ROUTES.dashboard} component={() => <Redirect to="/" />} />

      {/* Phase 2: Setup page with Sites, Checkpoints, Guards tabs */}
      <Route
        path={CLIENT_ROUTES.setup}
        component={() => (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            PublicComponent={AuthPage}
            ProtectedComponent={Setup}
          />
        )}
      />

      <Route
        path={CLIENT_ROUTES.patrols}
        component={() => (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            PublicComponent={AuthPage}
            ProtectedComponent={GuardHistory}
          />
        )}
      />

      <Route
        path={CLIENT_ROUTES.patrolDetail}
        component={() => (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            PublicComponent={AuthPage}
            ProtectedComponent={PatrolDetail}
          />
        )}
      />

      <Route
        path={CLIENT_ROUTES.guardHistory}
        component={() => (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            PublicComponent={AuthPage}
            ProtectedComponent={GuardHistory}
          />
        )}
      />

      <Route
        path={CLIENT_ROUTES.shiftScheduling}
        component={() =>
          FEATURES.SHIFT_SCHEDULING ? (
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
              PublicComponent={AuthPage}
              ProtectedComponent={ShiftScheduling}
            />
          ) : (
            <Redirect to="/" />
          )
        }
      />

      <Route
        path={CLIENT_ROUTES.profile}
        component={() => (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            PublicComponent={AuthPage}
            ProtectedComponent={ProfilePage}
          />
        )}
      />

      {/* ========================================================================= */}
      {/* ADMIN ROUTES - Heavy features with Mapbox, billing, etc. */}
      {/* ========================================================================= */}
      <Route
        path={CLIENT_ROUTES.adminDashboard}
        component={() => (
          <LazyRoute
            isAuthenticated={isAuthenticated}
            importPath="admin-dashboard"
            requireSuperAdmin={true}
          />
        )}
      />

      <Route
        path={CLIENT_ROUTES.adminCompany}
        component={() => (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            PublicComponent={AuthPage}
            ProtectedComponent={AdminCompanyViewer}
            requireSuperAdmin={true}
          />
        )}
      />

      <Route
        path={CLIENT_ROUTES.billing}
        component={() => (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            PublicComponent={AuthPage}
            ProtectedComponent={Billing}
          />
        )}
      />

      <Route
        path={CLIENT_ROUTES.internalManagement}
        component={() => (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            PublicComponent={AuthPage}
            ProtectedComponent={SuperAdmin}
            requireSuperAdmin={true}
          />
        )}
      />

      <Route
        path={CLIENT_ROUTES.customerManagement}
        component={() => (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            PublicComponent={AuthPage}
            ProtectedComponent={CustomerManagement}
            requireSuperAdmin={true}
          />
        )}
      />

      {/* ========================================================================= */}
      {/* ERROR ROUTES */}
      {/* ========================================================================= */}
          <Route component={() => <PublicRoute component={NotFound} />} />
        </Switch>
      </RoutedContent>
    </Suspense>
  );
}

function App() {
  // Only preload essential queries for auth - post-login features loaded later
  React.useEffect(() => {
    preloadCommonQueries();
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <OnboardingProvider>
          <TooltipProvider>
            <Toaster />
            <SubscriptionLapsedNotifier />
            <FloSupportWidget />
            <Router />
          </TooltipProvider>
        </OnboardingProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
